/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

const homepageContent = {
  brand: {
    name: "Paxia.ai",
    category: "Trading studio",
    promise: "Trade the plan you meant to trade.",
    description:
      "Paxia.ai is broker-connected software for traders who want accounts, risk controls, research, execution, review, and flexible AI workflows in one system.",
  },

  navigation: [
    { label: "Studio", href: "#studio" },
    { label: "Risk controls", href: "#risk-controls" },
    { label: "AI systems", href: "#ai-tools" },
    { label: "Automation", href: "#automation" },
    { label: "Trust", href: "#trust" },
  ],

  hero: {
    eyebrow: "Broker-connected trading studio",
    title: "Trade the plan you meant to trade.",
    subtitle:
      "Paxia.ai gives process-driven traders one broker-connected system for accounts, risk checks, research, execution, review, and flexible AI workflows built around their own playbook. The trade stays tied to the account, the limits, and the evidence behind it.",
    primaryCta: { label: "Request beta access", href: "#signup" },
    secondaryCta: { label: "Sign in", href: "/login" },
    trustLine: [
      "Account-aware risk checks",
      "Systems shaped to your playbook",
      "Research and execution stay together",
      "Alert, review, or auto-run",
    ],
    studioPreview: {
      account: {
        label: "Account status",
        value: "Ready",
        detail: "Risk profile configured",
      },
      risk: [
        { label: "Max daily loss", value: "3%" },
        { label: "Risk per trade", value: "0.5%" },
        { label: "Open positions", value: "3 max" },
      ],
      activity: [
        { label: "Trade check", value: "Within limits" },
        { label: "Automation", value: "Inside account rules" },
        { label: "Journal", value: "Ready for review" },
      ],
    },
  },

  problem: {
    id: "problem",
    label: "The real trading problem",
    title: "The edge gets lost when the workflow breaks apart.",
    body:
      "Most trading mistakes are not caused by a lack of ideas. They happen because the decision is split across broker windows, charts, calendars, news, notes, and journals. Paxia.ai exists to keep the trade, the account, and the evidence in the same operating environment.",
    items: [
      {
        icon: "ShieldAlert",
        title: "The broker accepts mistakes",
        body: "A trading terminal will happily accept a position your own rules would have rejected.",
      },
      {
        icon: "Layers",
        title: "The evidence is scattered",
        body: "Chart, account state, catalysts, and review notes usually live in different tools and never meet at the decision point.",
      },
      {
        icon: "Bot",
        title: "Automation raises the bar",
        body: "AI only helps when you can shape it, inspect it, monitor it, and decide how it runs.",
      },
    ],
  },

  workflow: {
    id: "workflow",
    label: "How Paxia.ai works",
    title: "Run the day in the right order.",
    body:
      "Paxia.ai is built around the sequence disciplined traders actually need: connect the account, define the limits, work the trade, check the context, review the result, and only then automate the parts worth repeating.",
    steps: [
      {
        icon: "Plug",
        title: "Connect accounts",
        body: "Bring supported broker accounts into one operating layer.",
      },
      {
        icon: "ShieldCheck",
        title: "Set limits",
        body: "Define daily loss, risk per trade, exposure, cooldowns, and size caps before live action.",
      },
      {
        icon: "LineChart",
        title: "Trade from Studio",
        body: "Keep chart, account, symbol, sizing, positions, and widgets in the same workspace.",
      },
      {
        icon: "Search",
        title: "Check context",
        body: "Pull in market, macro, news, options, and analysis context before committing risk.",
      },
      {
        icon: "BookOpenCheck",
        title: "Review, then automate",
        body: "Use history and journals to refine the process before handing repeatable work to AI.",
      },
    ],
  },

  riskControls: {
    id: "risk-controls",
    label: "Risk controls",
    title: "Set the limits before you need them.",
    body:
      "Every new order or AI-assisted action can be checked against the rules configured for that account before it moves toward the broker.",
    features: [
      "Daily loss cap",
      "Default and max risk per trade",
      "Open position limits",
      "Lot-size caps",
      "Same-symbol exposure checks",
      "Cooldowns after loss streaks",
      "Blocked-trade feedback",
    ],
    safetyNote:
      "Risk controls help enforce your own limits. They do not remove market risk, execution risk, or the possibility of loss.",
    exampleRules: [
      { label: "Max daily loss", value: "3%", tone: "warning" },
      { label: "Risk per trade", value: "0.5%", tone: "safe" },
      { label: "Cooldown", value: "30 min after loss streak", tone: "neutral" },
      { label: "Blocked trade", value: "Requested size exceeded max trade risk", tone: "blocked" },
    ],
  },

  studio: {
    id: "studio",
    label: "Trading studio",
    title: "Keep the account, chart, and decision in the same place.",
    body:
      "Dashboard shows the account state. Studio keeps the trade ticket, risk math, positions, catalysts, and notes close enough to act without losing context.",
    columns: [
      {
        title: "Dashboard",
        icon: "Gauge",
        points: [
          "Balances, equity, margin, positions, and pending orders",
          "Portfolio view or single-account focus",
          "Readiness signals before you click",
          "A control surface for the trading day",
        ],
      },
      {
        title: "Studio",
        icon: "CandlestickChart",
        points: [
          "Chart, account, symbol, and timeframe together",
          "Trade ticket with risk calculation",
          "Saved layouts for repeatable work",
          "Widgets for positions, news, calendar, journal, watchlist, and history",
        ],
      },
    ],
  },

  decisionLoop: {
    id: "decision-loop",
    label: "Decision loop",
    title: "Research before. Choose the execution mode. Review after.",
    body:
      "Paxia.ai is built to support the full trade lifecycle, not just the moment of execution.",
    lanes: [
      {
        icon: "SearchCheck",
        title: "Research",
        body: "Markets, equities, macro, news, earnings, options, insider activity, alerts, and analysis where supported.",
      },
      {
        icon: "ClipboardCheck",
        title: "Execution mode",
        body: "Choose whether a system reports, alerts, drafts the next step, requests review, or runs automatically.",
      },
      {
        icon: "History",
        title: "Review",
        body: "Close the loop with history, attribution, journaling, and performance context.",
      },
    ],
  },

  aiTools: {
    id: "ai-tools",
    label: "AI systems",
    title: "Build AI research and automation systems around your own trading playbook.",
    body:
      "Describe how you research, monitor, size, execute, and review. Paxia.ai turns that process into a configurable workflow with agents, tools, data sources, conditions, schedules, execution modes, and audit trails.",
    items: [
      {
        icon: "SearchCheck",
        title: "Your style, translated into systems",
        body: "Turn a plain-language description of your setups, process, and risk rules into one-off research jobs, scheduled routines, or always-on monitors.",
      },
      {
        icon: "Settings2",
        title: "Specialized agents, editable logic",
        body: "Use focused agents for research, monitoring, sizing, execution planning, review, and journaling, then shape the logic however you want.",
      },
      {
        icon: "Bot",
        title: "Execution mode is configurable",
        body: "Let a workflow report, alert, draft actions, request review, or auto-run inside your rules.",
      },
      {
        icon: "ShieldCheck",
        title: "Reusable playbooks",
        body: "Save systems for session prep, market research, signal monitoring, trade review, or broker-connected automation, then refine them as your style evolves.",
      },
    ],
    examples: [
      {
        title: "Pre-market playbook system",
        body: "Before the session starts, pull overnight moves, scheduled events, watchlist changes, open exposure, and available risk into one brief built around your session routine.",
      },
      {
        title: "Style-specific signal monitor",
        body: "Watch your symbols for the conditions that matter to your style, then alert, draft the trade note, or queue the next step.",
      },
      {
        title: "Review + journal system",
        body: "After the trade closes, collect the evidence, compare outcome to plan, and draft the journal entry around your own review process.",
      },
    ],
    researchInputs: {
      label: "Supported research context",
      title: "For equities, research workflows can pull more than price action.",
      body:
        "Equity research workflows can combine market, event, positioning, and narrative context so research is not reduced to a chart and a headline.",
      sources: [
        "Stock and ETF screens",
        "Earnings data",
        "Financial news",
        "Options chains",
        "SEC Form 4 insider activity",
        "Economic calendar",
        "Economic indicators",
        "COT positioning",
        "Knowledge graph context",
      ],
      groups: [
        {
          icon: "SearchCheck",
          title: "Find candidates",
          body:
            "Screen stocks and ETFs by price, trend, momentum, volatility, volume, and fundamental fields.",
        },
        {
          icon: "BookOpenCheck",
          title: "Check catalysts",
          body:
            "Review earnings, financial news, scheduled macro events, and SEC Form 4 insider activity where available.",
        },
        {
          icon: "LineChart",
          title: "Read the wider context",
          body:
            "Add options chains, COT positioning, economic indicators, and knowledge graph relationships when the idea needs more evidence.",
        },
      ],
    },
    systemBuilder: {
      label: "Example workflow",
      title: "One request can become a full research and automation system.",
      intro:
        "Describe the job once. Paxia.ai turns it into cooperating agents, tools, inputs, conditions, schedules, execution modes, and logs you can inspect.",
      prompt:
        "Build a workflow for a trend-following equities trader that creates a pre-market brief, watches 1h and 4h trend with RSI and EMA 50/100/200, looks for 5m and 15m pullback entries, adds news, earnings, options, insider activity, and fundamentals where available, alerts when conditions align, sizes risk at 0.3%-0.5%, and journals the rationale and outcome.",
      steps: [
        {
          title: "Split the work",
          body: "Break the job into research, monitoring, sizing, execution, and journaling agents that each handle part of the playbook.",
        },
        {
          title: "Attach the tools and data",
          body: "Use account state, positions, price, indicators, watchlists, news, calendar context, and supported equity research sources.",
        },
        {
          title: "Shape the logic",
          body: "Define the conditions, branches, schedules, risk bounds, outputs, and execution mode that match how you actually trade.",
        },
        {
          title: "Test and evolve",
          body: "Check missing data, edge cases, handoffs, and conflicts before running the system live, then improve it as the playbook evolves.",
        },
      ],
      safeguards: [
        "On-demand, scheduled, or event-driven runs",
        "Account-aware price and risk inputs",
        "News, earnings, and fundamentals context",
        "Branching logic and execution modes",
        "Alert, review, or auto-run policy",
        "Audit trail and run history",
      ],
    },
  },

  automation: {
    id: "automation",
    label: "Automation with control",
    title: "Turn strong workflows into reliable automation.",
    body:
      "Paxia.ai is built for flexible automation: start with the research and monitoring workflows that fit your style, add schedules and conditions, choose how each one behaves, and keep improving live systems as your process evolves.",
    ladder: [
      {
        title: "On-demand research",
        body: "Run a system when you need prep, idea validation, or post-trade review.",
      },
      {
        title: "Scheduled or event-driven",
        body: "Run every morning, watch live conditions, or trigger from new data and market events.",
      },
      {
        title: "Choose the execution mode",
        body: "Have the workflow report, alert, draft actions, route for review, or auto-run inside limits.",
      },
      {
        title: "Monitor and evolve",
        body: "Track runs, cost, reliability, outcomes, and drift as the system improves.",
      },
    ],
    trustCopy:
      "The point is flexibility with control: research, monitoring, and automation systems should fit your playbook, stay inspectable, and remain easy to evolve.",
  },

  trust: {
    id: "trust",
    label: "Trust and safety",
    title: "Software for control, not a promise of returns.",
    body:
      "Paxia.ai is software for traders. It is not financial advice, it does not hold client funds, and it does not remove market risk. Orders route to connected brokers while your configured controls help govern what the platform allows.",
    points: [
      "Software platform, not financial advice",
      "Client funds remain with the connected broker",
      "Trading still involves the risk of loss",
      "Configured rules help govern new actions",
      "Execution policy can be configured and audited",
      "Automation is reviewable and auditable",
    ],
  },

  finalCta: {
    id: "signup",
    title: "If you already value process, Paxia.ai will make sense very quickly.",
    body:
      "Beta access is open for traders who want connected accounts, enforceable limits, integrated research, chart-led execution, and AI research and automation workflows built around their own playbook.",
    primaryCta: { label: "Request beta access", href: "#signup" },
    secondaryCta: { label: "Sign in", href: "/login" },
    microcopy:
      "Built for traders who value discipline, context, and review more than hype. No financial advice. No guaranteed returns. No custody of client funds.",
  },

  footer: {
    contactEmail: "info@paxia.ai",
    links: [
      { label: "Sign in", href: "/login" },
      { label: "Investor presentation", href: "/invest" },
    ],
    disclaimer:
      "Paxia.ai is a software platform. Nothing on this site is financial advice. Trading involves risk, including loss of capital. Risk settings and automation controls do not guarantee trading outcomes.",
  },

  copyGuidance: {
    avoid: [
      "Beat the market with AI.",
      "Let AI trade for you.",
      "Guaranteed consistency.",
      "Safer trading without risk.",
      "Fully automated profits.",
      "Copy expert strategies in one click.",
    ],
    prefer: [
      "Run your trading process in one system.",
      "Keep the trade tied to the account and the rules.",
      "Automate repeatable work carefully and visibly.",
      "Review every result and improve the next decision.",
      "Give serious traders better operating discipline.",
    ],
  },
};

export default homepageContent;